/**
 * AIAssistant — slide-out chat panel for the admin dashboard.
 *
 * Features:
 * - Clickable smart-reply buttons extracted from AI responses
 * - Contextual action buttons on tool results (select file, publish, edit)
 * - One-click product/contract creation from Drive files
 */
import { useState, useEffect, useRef, useCallback } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import {
  Bot, X, Send, Loader2, AlertCircle, CheckCircle2,
  ChevronDown, Package, Code2, Layers, HardDrive,
  RefreshCw, Sparkles, ExternalLink, BarChart2, ShoppingCart,
  Trash2, Settings, ArrowRight, Eye, Upload, TrendingUp, Wallet, Clock,
  GitCommit, Terminal, FileText, Edit2, History,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAIProviderStore } from '@/stores/aiProviderStore'
import { SLASH_COMMANDS, searchCommands } from '@/lib/ai/slashCommands'
import { BROWSER_PROVIDER_ID } from '@/lib/ai/browserProvider'
import { runBrowserAgent } from '@/lib/ai/browserAgent'

// ─── Types ───────────────────────────────────────────────────────────────────

interface SmartReply {
  label: string
  prompt: string
  icon?: 'product' | 'contract' | 'bundle' | 'select' | 'publish' | 'yes' | 'no' | 'details' | 'continue'
}

interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
  toolResults?: ToolResultDisplay[]
  smartReplies?: SmartReply[]
  timestamp: number
}

interface ToolResultDisplay {
  tool: string
  success: boolean
  data?: Record<string, unknown>
  error?: string
}

interface RawToolResult {
  tool: string
  result: { success: boolean; data?: unknown; error?: string }
}

interface ChatResponse {
  data: {
    reply: string
    tool_results: RawToolResult[]
    provider_used: string
  }
}

/** Flatten the nested API shape into what the UI expects */
function flattenToolResults(raw: RawToolResult[]): ToolResultDisplay[] {
  return raw.map((r) => ({
    tool: r.tool,
    success: r.result.success,
    data: r.result.data as Record<string, unknown> | undefined,
    error: r.result.error,
  }))
}

// ─── Smart reply extraction ─────────────────────────────────────────────────

function generateSmartReplies(content: string, toolResults: ToolResultDisplay[]): SmartReply[] {
  const replies: SmartReply[] = []

  // After listing drive files — offer to create from each unlinked file
  const driveList = toolResults.find((t) => t.tool === 'list_drive_files' && t.success)
  if (driveList && Array.isArray(driveList.data)) {
    const unlinked = (driveList.data as Record<string, unknown>[])
      .filter((f) => !f.linked_to)
      .slice(0, 4)
    for (const file of unlinked) {
      const name = file.name as string
      const id = file.id as string
      replies.push({
        label: `Use "${name}"`,
        prompt: `Create a product using the file "${name}" (Drive ID: ${id}). Generate a good name, tagline, description, and pick a reasonable category and price.`,
        icon: 'product',
      })
    }
    if (unlinked.length > 0) {
      replies.push({
        label: 'Create a smart contract instead',
        prompt: 'Actually, I want to create a smart contract from one of those files. Which one should I use?',
        icon: 'contract',
      })
    }
  }

  // After creating a product/contract/bundle — offer next steps
  const createResult = toolResults.find((t) => t.tool.startsWith('create_') && t.success)
  if (createResult) {
    const data = createResult.data as Record<string, unknown>
    const id = data.id as string
    const type = createResult.tool.replace('create_', '')
    replies.push({
      label: 'Publish it now',
      prompt: `Publish the ${type} with ID "${id}".`,
      icon: 'publish',
    })
    replies.push({
      label: 'Edit in dashboard',
      prompt: `_NAVIGATE_:${data.edit_url}`,
      icon: 'details',
    })
    if (type === 'product') {
      replies.push({
        label: 'Create another product',
        prompt: 'I want to create another product. Show me Drive files again.',
        icon: 'product',
      })
      replies.push({
        label: 'Create a bundle with this',
        prompt: `List my smart contracts so I can bundle one with the product "${data.name}".`,
        icon: 'bundle',
      })
    }
  }

  // After listing products/contracts — offer actions
  const listProducts = toolResults.find((t) => t.tool === 'list_products' && t.success)
  if (listProducts && Array.isArray(listProducts.data)) {
    const items = (listProducts.data as Record<string, unknown>[]).slice(0, 3)
    for (const item of items) {
      if (item.status === 'draft') {
        replies.push({
          label: `Publish "${item.name}"`,
          prompt: `Publish the product with ID "${item.id}".`,
          icon: 'publish',
        })
      }
    }
  }

  const listContracts = toolResults.find((t) => t.tool === 'list_contracts' && t.success)
  if (listContracts && Array.isArray(listContracts.data)) {
    const items = (listContracts.data as Record<string, unknown>[]).slice(0, 3)
    for (const item of items) {
      if (item.status === 'draft') {
        replies.push({
          label: `Publish "${item.name}"`,
          prompt: `Publish the contract with ID "${item.id}".`,
          icon: 'publish',
        })
      }
    }
  }

  // After showing stats/analytics — offer deeper dives
  const statsResult = toolResults.find((t) => (t.tool === 'get_dashboard_stats' || t.tool === 'get_analytics') && t.success)
  if (statsResult) {
    replies.push({ label: 'View treasury', prompt: 'Show me the treasury data — revenue by chain and monthly P&L.', icon: 'details' })
    replies.push({ label: 'Show orders', prompt: 'List recent orders.', icon: 'continue' })
  }

  // If the AI is asking a question — parse for yes/no or choices
  if (content) {
    const lower = content.toLowerCase()

    // Yes/No questions
    const isQuestion = lower.includes('would you like') || lower.includes('do you want')
      || lower.includes('shall i') || lower.includes('should i')
      || lower.includes('ready to') || lower.includes('want me to')
      || lower.includes('continue with') || lower.includes('proceed with')
      || lower.includes('go ahead') || lower.includes('can i')
    if (isQuestion && replies.length === 0) {
      replies.push({ label: 'Yes, go ahead', prompt: 'Yes, proceed with that.', icon: 'yes' })
      replies.push({ label: 'No, show options', prompt: 'No, show me other options instead.', icon: 'no' })
    }

    // If the AI mentions a .zip file by name, offer to use it
    const zipMatches = content.match(/["']([^"']+\.zip)["']/gi)
    if (zipMatches && replies.length < 4) {
      for (const match of zipMatches.slice(0, 2)) {
        const filename = match.replace(/['"]/g, '')
        if (!replies.some((r) => r.label.includes(filename))) {
          replies.push({
            label: `Use ${filename}`,
            prompt: `Yes, use the file "${filename}" to create a product. Generate a good name, tagline, description, category, and price.`,
            icon: 'product',
          })
        }
      }
    }

    // If no tool results and no question detected, offer common actions as fallback
    if (replies.length === 0 && toolResults.length === 0 && content.length > 20) {
      replies.push({ label: 'Create a product', prompt: 'I want to create a new product. Show me Drive files.', icon: 'product' })
      replies.push({ label: 'Show Drive files', prompt: 'List all files in Google Drive.', icon: 'select' })
    }
  }

  return replies.slice(0, 6) // max 6 smart replies
}

// ─── Quick actions ───────────────────────────────────────────────────────────

const QUICK_ACTIONS = [
  { label: 'Create a Product', icon: Package, prompt: 'I want to create a new product. Show me what files are available on Google Drive so I can pick one.' },
  { label: 'Create a Smart Contract', icon: Code2, prompt: 'I want to create a new smart contract listing. What ZIP files are on Drive?' },
  { label: 'Create a Bundle', icon: Layers, prompt: 'I want to create a bundle. List my existing products and contracts so I can pick a combo.' },
  { label: 'Browse Drive Files', icon: HardDrive, prompt: 'List all files in our Google Drive folder with their link status.' },
  { label: 'Dashboard Stats', icon: BarChart2, prompt: 'Show me the dashboard overview — revenue, orders, products, buyers.' },
  { label: 'Recent Orders', icon: ShoppingCart, prompt: 'Show me the most recent orders.' },
]

// ─── Icon map for smart replies ──────────────────────────────────────────────

const REPLY_ICONS: Record<string, typeof Package> = {
  product: Package,
  contract: Code2,
  bundle: Layers,
  select: HardDrive,
  publish: Upload,
  yes: CheckCircle2,
  no: X,
  details: Eye,
  continue: ArrowRight,
}

// ─── Component ───────────────────────────────────────────────────────────────

export function AIAssistant({ open, onClose, initialPrompt }: { open: boolean; onClose: () => void; initialPrompt?: string }) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [showProviderPicker, setShowProviderPicker] = useState(false)
  const [slashMenuOpen, setSlashMenuOpen] = useState(false)
  const [slashFilter, setSlashFilter] = useState('')
  const [selectedSlashIdx, setSelectedSlashIdx] = useState(0)

  const {
    providers, selectedProvider, selectedModel,
    loading: providerLoading,
    detect, selectProvider,
  } = useAIProviderStore()

  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const navigate = useNavigate()

  // ─── Send message (defined before effects that reference it) ───────────

  const sendMessage = useCallback(async (text?: string) => {
    const content = text || input.trim()
    if (!content || loading) return

    // Handle navigation commands from smart replies
    if (content.startsWith('_NAVIGATE_:')) {
      navigate(content.replace('_NAVIGATE_:', ''))
      onClose()
      return
    }

    const userMsg: ChatMessage = { role: 'user', content, timestamp: Date.now() }
    setMessages((prev) => {
      // Remove smart replies from previous assistant message
      const updated = prev.map((m) => m.smartReplies ? { ...m, smartReplies: undefined } : m)
      return [...updated, userMsg]
    })
    setInput('')
    setLoading(true)

    try {
      const apiMessages = [...messages, userMsg].map((m) => ({
        role: m.role as 'user' | 'assistant',
        content: m.content,
      }))

      let data: ChatResponse['data']

      if (selectedProvider === BROWSER_PROVIDER_ID) {
        // Local on-device path — Chrome Built-in AI + WebMCP tool registry.
        // No network call, no token cost, sensitive context never leaves the browser.
        data = await runBrowserAgent(apiMessages)
      } else {
        const res = await fetch('/api/admin/ai/chat', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            messages: apiMessages,
            provider: selectedProvider,
            model: selectedModel,
          }),
        })

        if (!res.ok) {
          const errData = await res.json().catch(() => null)
          throw new Error(errData?.error?.message || `HTTP ${res.status}`)
        }

        const json: ChatResponse = await res.json()
        if (!json?.data) throw new Error('Empty response from AI agent')
        data = json.data
      }

      const flattened = data.tool_results?.length > 0 ? flattenToolResults(data.tool_results) : []
      const toolResults = flattened.length > 0 ? flattened : undefined
      const smartReplies = generateSmartReplies(data.reply || '', flattened)

      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: data.reply || '',
        toolResults,
        smartReplies: smartReplies.length > 0 ? smartReplies : undefined,
        timestamp: Date.now(),
      }])
    } catch (err) {
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: `Error: ${err instanceof Error ? err.message : 'Something went wrong'}`,
        timestamp: Date.now(),
      }])
    } finally {
      setLoading(false)
    }
  }, [input, loading, messages, selectedProvider, selectedModel, navigate, onClose])

  // ─── Effects ──────────────────────────────────────────────────────────────

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 300)
  }, [open])

  // Auto-send initial prompt when opened with one (e.g. from file card click)
  const initialPromptSent = useRef(false)
  useEffect(() => {
    if (open && initialPrompt && selectedProvider && !initialPromptSent.current) {
      initialPromptSent.current = true
      setTimeout(() => sendMessage(initialPrompt), 400)
    }
    if (!open) initialPromptSent.current = false
  }, [open, initialPrompt, selectedProvider, sendMessage])

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight
  }, [messages, loading])

  function handleKeyDown(e: React.KeyboardEvent) {
    // Handle slash menu navigation
    if (slashMenuOpen) {
      const filtered = searchCommands(slashFilter)
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedSlashIdx(Math.max(0, selectedSlashIdx - 1))
        return
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedSlashIdx(Math.min(filtered.length - 1, selectedSlashIdx + 1))
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        const selected = filtered[selectedSlashIdx]
        if (selected) {
          setInput('')
          setSlashMenuOpen(false)
          // Send the command prompt
          sendMessage(selected.prompt)
        }
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setSlashMenuOpen(false)
        return
      }
    }
    // Normal message send
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const activeProvider = providers.find((p) => p.id === selectedProvider)
  const hasProvider = !!selectedProvider

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />

          <motion.div
            initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            className="fixed top-0 right-0 bottom-0 z-50 w-full max-w-[480px] flex flex-col bg-[#0A0A0A] border-l border-[#1E1E1E] shadow-2xl"
          >
            {/* ── Header ──────────────────────────────────────────────────── */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#1E1E1E]">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[rgba(232,133,12,0.12)] border border-[rgba(232,133,12,0.2)] flex items-center justify-center">
                  <Sparkles size={16} className="text-[#E8850C]" />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-[#F5F5F5]">AI Agent</h2>
                  {activeProvider ? (
                    <button
                      onClick={() => setShowProviderPicker(!showProviderPicker)}
                      className="flex items-center gap-1 text-[10px] text-[#666] hover:text-[#999] transition-colors"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      {activeProvider.name}
                      {activeProvider.model && ` · ${activeProvider.model}`}
                      <ChevronDown size={10} />
                    </button>
                  ) : providerLoading ? (
                    <span className="text-[10px] text-[#444]">Detecting providers...</span>
                  ) : (
                    <button
                      onClick={() => setShowProviderPicker(true)}
                      className="text-[10px] text-[#f87171] hover:text-[#ff9999] transition-colors underline underline-offset-2"
                    >
                      No provider — click to configure
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setShowProviderPicker(!showProviderPicker)}
                  className={[
                    'p-1.5 rounded-lg transition-colors',
                    showProviderPicker
                      ? 'text-[#E8850C] bg-[rgba(232,133,12,0.1)]'
                      : 'text-[#666] hover:text-[#F5F5F5] hover:bg-[#141414]',
                  ].join(' ')}
                  title="Configure LLM provider"
                >
                  <Settings size={14} />
                </button>
                {messages.length > 0 && (
                  <button onClick={() => setMessages([])} className="p-1.5 text-[#666] hover:text-[#F5F5F5] rounded-lg hover:bg-[#141414] transition-colors" title="Clear chat">
                    <Trash2 size={14} />
                  </button>
                )}
                <button onClick={onClose} className="p-1.5 text-[#666] hover:text-[#F5F5F5] rounded-lg hover:bg-[#141414] transition-colors">
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* ── Provider picker ─────────────────────────────────────────── */}
            <AnimatePresence>
              {showProviderPicker && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }} className="overflow-hidden border-b border-[#1E1E1E]"
                >
                  <div className="p-3 space-y-1.5">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[10px] text-[#555] uppercase tracking-widest font-semibold">LLM Provider</p>
                      <button
                        onClick={() => { useAIProviderStore.getState().reset(); detect() }}
                        className="text-[10px] text-[#666] hover:text-[#E8850C] flex items-center gap-1 transition-colors"
                      >
                        <RefreshCw size={10} /> Re-detect
                      </button>
                    </div>
                    {providers.map((p) => (
                      <button
                        key={p.id}
                        disabled={!p.available}
                        onClick={() => { selectProvider(p.id, p.model); setShowProviderPicker(false) }}
                        className={[
                          'w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition-colors text-xs',
                          p.available
                            ? p.id === selectedProvider
                              ? 'bg-[rgba(232,133,12,0.1)] border border-[rgba(232,133,12,0.3)] text-[#E8850C]'
                              : 'bg-[#111] border border-[#1A1A1A] text-[#CCC] hover:border-[#333]'
                            : 'bg-[#0D0D0D] border border-[#141414] text-[#444] cursor-not-allowed',
                        ].join(' ')}
                      >
                        <div>
                          <p className="font-medium">{p.name}</p>
                          <p className="text-[10px] mt-0.5 opacity-70">{p.detail}</p>
                        </div>
                        {p.available && <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* ── No provider setup guide ────────────────────────────────── */}
            {!providerLoading && !hasProvider && (
              <div className="mx-4 mt-3 space-y-2">
                <div className="px-3 py-2.5 bg-[#1a0a0a] border border-[#3d1515] rounded-lg">
                  <div className="flex items-center gap-2 text-[#f87171] text-xs mb-2">
                    <AlertCircle size={13} />
                    <span className="font-medium">No LLM provider connected</span>
                  </div>
                  <p className="text-[10px] text-[#888] mb-3">Choose one of these options to enable the AI Agent:</p>
                  <div className="space-y-2 text-[11px]">
                    <div className="px-2.5 py-2 bg-[#111] border border-[#1A1A1A] rounded-lg">
                      <p className="text-[#CCC] font-medium mb-1">1. Ollama (Local, Free)</p>
                      <p className="text-[#666]">Open the Ollama app, then run:</p>
                      <code className="block mt-1 text-[10px] text-[#E8850C] font-mono">ollama pull qwen2.5</code>
                    </div>
                    <div className="px-2.5 py-2 bg-[#111] border border-[#1A1A1A] rounded-lg">
                      <p className="text-[#CCC] font-medium mb-1">2. Claude API Key</p>
                      <p className="text-[#666]">Add to <span className="font-mono text-[#888]">.env.local</span>:</p>
                      <code className="block mt-1 text-[10px] text-[#E8850C] font-mono">ANTHROPIC_API_KEY=sk-ant-...</code>
                    </div>
                    <div className="px-2.5 py-2 bg-[#111] border border-[#1A1A1A] rounded-lg">
                      <p className="text-[#CCC] font-medium mb-1">3. OpenRouter (Multi-model)</p>
                      <p className="text-[#666]">Add to <span className="font-mono text-[#888]">.env.local</span>:</p>
                      <code className="block mt-1 text-[10px] text-[#E8850C] font-mono">OPENROUTER_API_KEY=sk-or-...</code>
                    </div>
                  </div>
                  <button
                    onClick={() => { useAIProviderStore.getState().reset(); detect() }}
                    className="mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-[#E8850C] hover:bg-[#D4750A] text-white text-xs font-medium rounded-lg transition-colors"
                  >
                    <RefreshCw size={11} />
                    Re-detect Providers
                  </button>
                </div>
              </div>
            )}

            {/* ── Messages ────────────────────────────────────────────────── */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
              {messages.length === 0 && !providerLoading && (
                <div className="flex flex-col items-center pt-6">
                  <div className="w-14 h-14 rounded-2xl bg-[rgba(232,133,12,0.08)] border border-[rgba(232,133,12,0.15)] flex items-center justify-center mb-4">
                    <Bot size={24} className="text-[#E8850C]" />
                  </div>
                  <p className="text-sm font-medium text-[#F5F5F5] mb-1">SaaSFarm Agent</p>
                  <p className="text-xs text-[#666] mb-5 text-center max-w-[300px]">
                    Full admin control via natural language. Create products, manage orders, view analytics — all through chat.
                  </p>
                  <div className="w-full grid grid-cols-2 gap-2">
                    {QUICK_ACTIONS.map(({ label, icon: Icon, prompt }) => (
                      <button
                        key={label}
                        onClick={() => sendMessage(prompt)}
                        disabled={!hasProvider || loading}
                        className="flex items-center gap-2.5 px-3 py-2.5 bg-[#111] border border-[#1A1A1A] rounded-xl text-left hover:border-[rgba(232,133,12,0.3)] hover:bg-[rgba(232,133,12,0.04)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed group"
                      >
                        <Icon size={13} className="text-[#888] group-hover:text-[#E8850C] transition-colors shrink-0" />
                        <span className="text-[11px] font-medium text-[#CCC] group-hover:text-[#F5F5F5] leading-tight">{label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((msg, i) => (
                <MessageBubble
                  key={i}
                  message={msg}
                  onNavigate={navigate}
                  onSmartReply={sendMessage}
                  isLatest={i === messages.length - 1}
                  loading={loading}
                />
              ))}

              {loading && (
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-lg bg-[rgba(232,133,12,0.1)] border border-[rgba(232,133,12,0.15)] flex items-center justify-center shrink-0 mt-0.5">
                    <Bot size={13} className="text-[#E8850C]" />
                  </div>
                  <div className="flex items-center gap-2 py-2.5 px-3.5 bg-[#111] border border-[#1A1A1A] rounded-xl rounded-tl-sm">
                    <Loader2 size={12} className="animate-spin text-[#E8850C]" />
                    <span className="text-xs text-[#888]">Working...</span>
                  </div>
                </div>
              )}
            </div>

            {/* ── Slash Command Menu ──────────────────────────────────────── */}
            {slashMenuOpen && (
              <div className="border-t border-[#1E1E1E] px-4 py-2">
                <div className="bg-[#111] border border-[#1E1E1E] rounded-lg overflow-hidden">
                  {(() => {
                    const filtered = searchCommands(slashFilter)
                    if (filtered.length === 0) {
                      return (
                        <div className="px-4 py-3 text-center text-[11px] text-[#666]">
                          No commands found
                        </div>
                      )
                    }
                    return (
                      <div className="max-h-[280px] overflow-y-auto">
                        {filtered.map((cmd, idx) => {
                          const isSelected = idx === selectedSlashIdx
                          const IconComponent = SLASH_COMMANDS.find((c) => c.command === cmd.command)?.icon || 'HelpCircle'
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          const icons: Record<string, any> = {
                            BarChart2,
                            TrendingUp,
                            Wallet,
                            ShoppingCart,
                            Clock,
                            Package,
                            Code2,
                            Layers,
                            HardDrive,
                            Plus: Package,
                            Send: Upload,
                            Eye,
                            RotateCcw: RefreshCw,
                            BookOpen: Package,
                            DollarSign: Package,
                            Image: Package,
                            Settings,
                            Key: Upload,
                            AlertCircle,
                            HelpCircle: Sparkles,
                          }
                          const Icon = icons[IconComponent as string] || Package
                          return (
                            <button
                              key={`${cmd.command}-${idx}`}
                              onClick={() => {
                                setInput('')
                                setSlashMenuOpen(false)
                                sendMessage(cmd.prompt)
                              }}
                              onMouseEnter={() => setSelectedSlashIdx(idx)}
                              className={`w-full flex items-start gap-3 px-4 py-2.5 text-left transition-colors ${
                                isSelected
                                  ? 'bg-[rgba(232,133,12,0.12)] border-l-2 border-[#E8850C]'
                                  : 'hover:bg-[#0D0D0D] border-l-2 border-transparent'
                              }`}
                            >
                              <Icon size={14} className={`mt-0.5 shrink-0 ${isSelected ? 'text-[#E8850C]' : 'text-[#666]'}`} />
                              <div className="min-w-0 flex-1">
                                <p className={`text-sm font-medium ${isSelected ? 'text-[#E8850C]' : 'text-[#CCC]'}`}>
                                  /{cmd.command}
                                </p>
                                <p className="text-[11px] text-[#777] mt-0.5">{cmd.description}</p>
                              </div>
                            </button>
                          )
                        })}
                      </div>
                    )
                  })()}
                </div>
              </div>
            )}

            {/* ── Input ───────────────────────────────────────────────────── */}
            <div className="border-t border-[#1E1E1E] px-4 py-3">
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => {
                    const newInput = e.target.value
                    setInput(newInput)
                    // Handle slash command menu
                    if (newInput.startsWith('/')) {
                      setSlashMenuOpen(true)
                      setSlashFilter(newInput.slice(1))
                      setSelectedSlashIdx(0)
                    } else {
                      setSlashMenuOpen(false)
                    }
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder={hasProvider ? 'Tell me what to do...' : 'No LLM provider detected'}
                  disabled={!hasProvider || loading}
                  rows={1}
                  className="flex-1 resize-none bg-[#111] border border-[#1E1E1E] rounded-xl px-4 py-3 text-sm text-[#F5F5F5] placeholder:text-[#444] focus:outline-none focus:border-[rgba(232,133,12,0.4)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ minHeight: '44px', maxHeight: '120px' }}
                  onInput={(e) => {
                    const el = e.currentTarget
                    el.style.height = '44px'
                    el.style.height = Math.min(el.scrollHeight, 120) + 'px'
                  }}
                />
                <button
                  onClick={() => sendMessage()}
                  disabled={!input.trim() || !hasProvider || loading}
                  className="w-10 h-10 rounded-xl bg-[#E8850C] hover:bg-[#D4750A] disabled:bg-[#1E1E1E] disabled:text-[#444] text-white flex items-center justify-center transition-colors shrink-0"
                >
                  <Send size={15} />
                </button>
              </div>
              <p className="text-[10px] text-[#444] mt-2 text-center">
                Items are created as drafts. {activeProvider ? `Using ${activeProvider.name}` : ''}
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

// ─── Message bubble ──────────────────────────────────────────────────────────

function MessageBubble({
  message,
  onNavigate,
  onSmartReply,
  isLatest,
  loading,
}: {
  message: ChatMessage
  onNavigate: (path: string) => void
  onSmartReply: (text: string) => void
  isLatest: boolean
  loading: boolean
}) {
  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] px-4 py-2.5 bg-[#E8850C] text-white text-sm rounded-xl rounded-br-sm">
          {message.content}
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-start gap-3">
      <div className="w-7 h-7 rounded-lg bg-[rgba(232,133,12,0.1)] border border-[rgba(232,133,12,0.15)] flex items-center justify-center shrink-0 mt-0.5">
        <Bot size={13} className="text-[#E8850C]" />
      </div>
      <div className="max-w-[90%] space-y-2">
        {message.toolResults?.map((tr, i) => (
          <ToolResultCard key={i} result={tr} onNavigate={onNavigate} onAction={onSmartReply} loading={loading} />
        ))}
        {message.content && (
          <div className="px-4 py-2.5 bg-[#111] border border-[#1A1A1A] rounded-xl rounded-tl-sm text-sm text-[#DDD] leading-relaxed whitespace-pre-wrap">
            {message.content}
          </div>
        )}
        {/* Smart reply buttons — only show on the latest message and when not loading */}
        {isLatest && !loading && message.smartReplies && message.smartReplies.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {message.smartReplies.map((reply, i) => {
              const Icon = REPLY_ICONS[reply.icon || 'continue'] || ArrowRight
              return (
                <button
                  key={i}
                  onClick={() => onSmartReply(reply.prompt)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#111] border border-[#1E1E1E] rounded-full text-[11px] font-medium text-[#CCC] hover:border-[rgba(232,133,12,0.4)] hover:text-[#E8850C] hover:bg-[rgba(232,133,12,0.06)] transition-all active:scale-95"
                >
                  <Icon size={11} />
                  {reply.label}
                </button>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Tool result card ────────────────────────────────────────────────────────

function ToolResultCard({
  result,
  onNavigate,
  onAction,
  loading,
}: {
  result: ToolResultDisplay
  onNavigate: (path: string) => void
  onAction: (prompt: string) => void
  loading: boolean
}) {
  // The shape of `data` is determined per-tool by the AI runtime — sometimes
  // it's a record (single result), sometimes an array (list_* tools). Casting
  // once here keeps the dozens of render sites readable; the conditional
  // Array.isArray() guards downstream still narrow correctly because they
  // operate on the runtime value, not the type.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const data: any = result.data || {}
  const isCreate = result.tool.startsWith('create_')
  const isUpdate = result.tool.startsWith('update_') || result.tool.startsWith('publish_')
  const isDelete = result.tool.startsWith('delete_') || result.tool === 'refund_order'

  if (!result.success) {
    return (
      <div className="px-3 py-2.5 bg-[#1a0a0a] border border-[#3d1515] rounded-lg">
        <div className="flex items-center gap-2 text-[#f87171] text-xs">
          <AlertCircle size={12} />
          <span className="font-medium">{formatToolName(result.tool)}</span>
        </div>
        <p className="text-[11px] text-[#f87171]/70 mt-1">{result.error}</p>
      </div>
    )
  }

  // ── Drive file list with clickable file selection ──────────────────────
  if (result.tool === 'list_drive_files' && Array.isArray(data)) {
    const files = data as Record<string, unknown>[]
    return (
      <div className="px-3 py-2.5 bg-[#0D0D0D] border border-[#1A1A1A] rounded-lg">
        <p className="text-[10px] text-[#555] uppercase tracking-widest font-semibold mb-2">
          Drive Files ({files.length})
        </p>
        <div className="space-y-1 max-h-[250px] overflow-y-auto">
          {files.map((file, i) => {
            const linked = file.linked_to as string | null
            const name = file.name as string
            const id = file.id as string
            const sizeKb = Math.round((file.size_bytes as number) / 1024)
            const sizeLabel = sizeKb > 1024 ? `${(sizeKb / 1024).toFixed(1)} MB` : `${sizeKb} KB`

            return (
              <div key={i} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg ${linked ? 'opacity-50' : 'hover:bg-[#141414]'}`}>
                <HardDrive size={11} className={linked ? 'text-[#444]' : 'text-[#E8850C]'} />
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] text-[#CCC] truncate">{name}</p>
                  <p className="text-[9px] text-[#555]">
                    {sizeLabel}
                    {linked && <span className="ml-1 text-emerald-500/60">{linked}</span>}
                  </p>
                </div>
                {!linked && !loading && (
                  <button
                    onClick={() => onAction(`Create a product using the file "${name}" (Drive ID: ${id}). Generate a good name, tagline, description, and pick a reasonable category and price.`)}
                    className="shrink-0 px-2 py-1 text-[9px] font-medium text-[#E8850C] bg-[rgba(232,133,12,0.08)] border border-[rgba(232,133,12,0.2)] rounded-md hover:bg-[rgba(232,133,12,0.15)] transition-colors"
                  >
                    Use this
                  </button>
                )}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  // ── Create/update/delete results ───────────────────────────────────────
  if (isCreate || isUpdate || isDelete) {
    const editUrl = data.edit_url as string
    const icon = isDelete ? <Trash2 size={12} /> : <CheckCircle2 size={12} />
    const label = isDelete ? (data.deleted ? 'Deleted' : 'Unpublished') : isUpdate ? 'Updated' : 'Created as Draft'

    return (
      <div className="px-3 py-2.5 bg-[rgba(16,185,129,0.06)] border border-[rgba(16,185,129,0.15)] rounded-lg">
        <div className="flex items-center gap-2 text-emerald-400 text-xs mb-1.5">
          {icon}
          <span className="font-medium">{label}</span>
          <span className="text-[#666]">({formatToolName(result.tool)})</span>
        </div>
        <div className="space-y-0.5 text-[11px] text-[#AAA]">
          {data.name && <p><span className="text-[#666]">Name:</span> {data.name as string}</p>}
          {data.price_usd != null && <p><span className="text-[#666]">Price:</span> ${(data.price_usd as number).toLocaleString()}</p>}
          {data.message && <p className="text-[#888]">{data.message as string}</p>}
          {data.product_name && <p><span className="text-[#666]">Product:</span> {data.product_name as string}</p>}
          {data.contract_name && <p><span className="text-[#666]">Contract:</span> {data.contract_name as string}</p>}
          {data.new_status && <p><span className="text-[#666]">Status:</span> {data.new_status as string}</p>}
        </div>
        {editUrl && (
          <button
            onClick={() => onNavigate(editUrl)}
            className="mt-2 flex items-center gap-1.5 text-[11px] text-[#E8850C] hover:text-[#F5A623] font-medium transition-colors"
          >
            <ExternalLink size={10} /> Open in editor
          </button>
        )}
      </div>
    )
  }

  // ── Product/contract list with inline actions ──────────────────────────
  if ((result.tool === 'list_products' || result.tool === 'list_contracts' || result.tool === 'list_bundles') && Array.isArray(data)) {
    const items = data as Record<string, unknown>[]
    const typeLabel = result.tool.replace('list_', '').replace('s', '')
    return (
      <div className="px-3 py-2.5 bg-[#0D0D0D] border border-[#1A1A1A] rounded-lg">
        <p className="text-[10px] text-[#555] uppercase tracking-widest font-semibold mb-1.5">
          {formatToolName(result.tool)} ({items.length})
        </p>
        <div className="space-y-1 max-h-[200px] overflow-y-auto">
          {items.slice(0, 15).map((item, i) => (
            <div key={i} className="flex items-center gap-2 text-[11px] text-[#AAA] py-0.5">
              <span className="w-1 h-1 rounded-full bg-[#444] shrink-0" />
              <span className="truncate flex-1">{item.name as string}</span>
              {Boolean(item.status) && (
                <span className={`text-[9px] px-1.5 py-0.5 rounded-full shrink-0 ${
                  item.status === 'published' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-[#1A1A1A] text-[#666]'
                }`}>
                  {item.status as string}
                </span>
              )}
              {item.price_usd != null && (
                <span className="text-[10px] text-[#555] shrink-0">${(item.price_usd as number).toLocaleString()}</span>
              )}
              {item.status === 'draft' && !loading && (
                <button
                  onClick={() => onAction(`Publish the ${typeLabel} with ID "${item.id}".`)}
                  className="shrink-0 text-[9px] text-[#E8850C] hover:text-[#F5A623] font-medium transition-colors"
                >
                  Publish
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    )
  }

  // ── Stats / object results ─────────────────────────────────────────────
  if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
    const entries = Object.entries(data).filter(([, v]) => typeof v !== 'object' || v === null)
    if (entries.length > 0) {
      return (
        <div className="px-3 py-2.5 bg-[#0D0D0D] border border-[#1A1A1A] rounded-lg">
          <p className="text-[10px] text-[#555] uppercase tracking-widest font-semibold mb-1.5">
            {formatToolName(result.tool)}
          </p>
          <div className="space-y-0.5">
            {entries.slice(0, 12).map(([k, v]) => (
              <div key={k} className="text-[11px] flex justify-between">
                <span className="text-[#666]">{k.replace(/_/g, ' ')}</span>
                <span className="text-[#AAA] font-mono">
                  {typeof v === 'number' ? (k.includes('revenue') || k.includes('price') ? `$${v.toLocaleString()}` : v.toLocaleString()) : String(v)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )
    }
  }

  // ── Code: read file ────────────────────────────────────────────────────────
  if (result.tool === 'code_read_file') {
    return (
      <div className="px-3 py-2.5 bg-[#0D0D0D] border border-[#1A1A1A] rounded-lg">
        <p className="text-[10px] text-[#555] uppercase tracking-widest font-semibold mb-1.5 flex items-center gap-2">
          <FileText size={10} /> {data.file_path as string}
        </p>
        <pre className="text-[10px] text-[#AAA] font-mono max-h-[300px] overflow-auto whitespace-pre-wrap break-words bg-[#000] p-2 rounded border border-[#222] leading-tight">
          {data.content as string}
        </pre>
        {data.truncated && <p className="text-[9px] text-[#666] mt-1">Showing lines {data.line_start}-{data.line_end} of {data.total_lines}</p>}
      </div>
    )
  }

  // ── Code: edit file (diff preview) ──────────────────────────────────────
  if (result.tool === 'code_edit_file') {
    return (
      <div className="px-3 py-2.5 bg-[#0D0D0D] border border-[#1A1A1A] rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] text-[#555] uppercase tracking-widest font-semibold flex items-center gap-2">
            <Edit2 size={10} /> {data.file_path as string}
          </p>
          <span className="text-[9px] px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-400 font-medium">Applied</span>
        </div>
        <div className="grid grid-cols-2 gap-2 text-[10px] font-mono max-h-[200px] overflow-auto">
          <div className="bg-[#1a0a0a] p-2 rounded border border-[#3d1515]">
            <p className="text-red-400 font-semibold mb-1 text-[9px]">- Removed</p>
            <p className="text-red-300 break-words whitespace-pre-wrap">{(data.old_snippet as string)?.slice(0, 80)}</p>
          </div>
          <div className="bg-[#0a1a0a] p-2 rounded border border-[#153d15]">
            <p className="text-emerald-400 font-semibold mb-1 text-[9px]">+ Added</p>
            <p className="text-emerald-300 break-words whitespace-pre-wrap">{(data.new_snippet as string)?.slice(0, 80)}</p>
          </div>
        </div>
      </div>
    )
  }

  // ── Code: git diff ──────────────────────────────────────────────────────
  if (result.tool === 'code_git_diff') {
    const diff = data.diff as string
    const lines = diff.split('\n')
    return (
      <div className="px-3 py-2.5 bg-[#0D0D0D] border border-[#1A1A1A] rounded-lg">
        <p className="text-[10px] text-[#555] uppercase tracking-widest font-semibold mb-1.5">Git Diff</p>
        <pre className="text-[9px] font-mono max-h-[300px] overflow-auto leading-tight">
          {lines.map((line, i) => {
            let color = '#AAA'
            if (line.startsWith('+')) color = '#10b981'
            else if (line.startsWith('-')) color = '#ef4444'
            else if (line.startsWith('@@')) color = '#3b82f6'
            else if (line.startsWith('\\')) color = '#888'
            return <div key={i} style={{ color }} className="whitespace-pre-wrap break-words">{line}</div>
          })}
        </pre>
      </div>
    )
  }

  // ── Code: git status ────────────────────────────────────────────────────
  if (result.tool === 'code_git_status') {
    const files = (data.files as Array<{ status: string; file: string }>) || []
    const clean = data.clean as boolean
    if (clean) {
      return (
        <div className="px-3 py-2.5 bg-[rgba(16,185,129,0.06)] border border-[rgba(16,185,129,0.15)] rounded-lg">
          <p className="text-[11px] text-emerald-400 font-medium">✓ Working directory is clean</p>
        </div>
      )
    }
    return (
      <div className="px-3 py-2.5 bg-[#0D0D0D] border border-[#1A1A1A] rounded-lg">
        <p className="text-[10px] text-[#555] uppercase tracking-widest font-semibold mb-1.5">Git Status ({files.length})</p>
        <div className="space-y-1 max-h-[200px] overflow-auto">
          {files.map((f, i) => {
            const statusColor = f.status === 'M' ? 'text-yellow-500' : f.status === 'A' ? 'text-emerald-500' : f.status === 'D' ? 'text-red-500' : 'text-[#666]'
            return (
              <div key={i} className="flex items-center gap-2 text-[11px]">
                <span className={`font-bold w-5 ${statusColor}`}>{f.status}</span>
                <span className="text-[#AAA] truncate flex-1">{f.file}</span>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  // ── Code: git commit ────────────────────────────────────────────────────
  if (result.tool === 'code_git_commit') {
    return (
      <div className="px-3 py-2.5 bg-[rgba(16,185,129,0.06)] border border-[rgba(16,185,129,0.15)] rounded-lg">
        <div className="flex items-center gap-2 text-emerald-400 text-xs mb-1.5">
          <GitCommit size={12} />
          <span className="font-medium">Committed</span>
          <code className="text-[9px] bg-[rgba(16,185,129,0.1)] px-1.5 py-0.5 rounded font-mono">{(data.commit_hash as string)?.slice(0, 7)}</code>
        </div>
        <p className="text-[11px] text-[#AAA]">{data.message as string}</p>
        {data.stat && <p className="text-[9px] text-[#666] mt-1 font-mono">{(data.stat as string).slice(0, 100)}</p>}
      </div>
    )
  }

  // ── Code: delete file ───────────────────────────────────────────────────
  if (result.tool === 'code_delete_file') {
    return (
      <div className="px-3 py-2.5 bg-[rgba(239,68,68,0.06)] border border-[rgba(239,68,68,0.15)] rounded-lg">
        <div className="flex items-center gap-2 text-red-400 text-xs">
          <Trash2 size={12} />
          <span className="font-medium">Deleted</span>
          <code className="text-[9px] bg-[rgba(239,68,68,0.1)] px-1.5 py-0.5 rounded font-mono flex-1">{data.deleted as string}</code>
        </div>
      </div>
    )
  }

  // ── Code: git log ───────────────────────────────────────────────────────
  if (result.tool === 'code_git_log') {
    const commits = (data.commits as Array<Record<string, unknown>>) || []
    return (
      <div className="px-3 py-2.5 bg-[#0D0D0D] border border-[#1A1A1A] rounded-lg">
        <p className="text-[10px] text-[#555] uppercase tracking-widest font-semibold mb-1.5 flex items-center gap-2">
          <History size={10} /> Git History ({commits.length})
        </p>
        <div className="space-y-1.5 max-h-[250px] overflow-auto">
          {commits.map((commit, i) => (
            <div key={i} className="text-[10px] text-[#AAA] font-mono">
              <div className="flex items-center gap-2">
                <code className="text-[#E8850C] font-bold">{(commit.hash as string)?.slice(0, 7)}</code>
                <span className="text-[#AAA] flex-1 truncate">{commit.message as string}</span>
              </div>
              {Boolean(commit.author) && <p className="text-[9px] text-[#666] ml-9">{commit.author as string} • {commit.date as string}</p>}
            </div>
          ))}
        </div>
      </div>
    )
  }

  // ── Code: run command ───────────────────────────────────────────────────
  if (result.tool === 'code_run_command') {
    const stdout = data.stdout as string
    const stderr = data.stderr as string
    const exitCode = data.exit_code as number
    return (
      <div className="px-3 py-2.5 bg-[#0D0D0D] border border-[#1A1A1A] rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <Terminal size={11} className="text-[#E8850C]" />
          <code className="text-[10px] text-[#AAA] font-mono flex-1">{data.command as string}</code>
          <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${exitCode === 0 ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
            {exitCode === 0 ? 'Success' : `Exit ${exitCode}`}
          </span>
        </div>
        {stdout && (
          <pre className="text-[9px] text-[#AAA] font-mono max-h-[200px] overflow-auto bg-[#000] p-2 rounded border border-[#222] mb-1 leading-tight">
            {(stdout as string).slice(0, 1000)}
          </pre>
        )}
        {stderr && (
          <pre className="text-[9px] text-red-400 font-mono max-h-[100px] overflow-auto bg-[#1a0a0a] p-2 rounded border border-[#3d1515] leading-tight">
            {(stderr as string).slice(0, 500)}
          </pre>
        )}
      </div>
    )
  }

  // ── Code: file list / search ────────────────────────────────────────────
  if ((result.tool === 'code_list_files' || result.tool === 'code_search') && Array.isArray(data.files)) {
    const items = (data.files as Array<string | Record<string, unknown>>) || []
    const isSearch = result.tool === 'code_search'
    return (
      <div className="px-3 py-2.5 bg-[#0D0D0D] border border-[#1A1A1A] rounded-lg">
        <p className="text-[10px] text-[#555] uppercase tracking-widest font-semibold mb-1.5">
          {isSearch ? 'Search Results' : 'Files'} ({items.length})
        </p>
        <div className="space-y-1 max-h-[250px] overflow-y-auto">
          {items.slice(0, 20).map((item, i) => {
            if (isSearch && typeof item === 'object') {
              const searchItem = item as Record<string, unknown>
              return (
                <div key={i} className="text-[10px] text-[#AAA] font-mono">
                  <p className="text-[#E8850C]">{searchItem.file as string}:{String(searchItem.line ?? '')}</p>
                  <p className="text-[#666] truncate ml-4">{(searchItem.content as string)?.slice(0, 80)}</p>
                </div>
              )
            }
            return (
              <div key={i} className="flex items-center gap-2 text-[11px] text-[#AAA]">
                <FileText size={10} className="text-[#555] shrink-0" />
                <span className="truncate">{typeof item === 'string' ? item : (item as Record<string, unknown>).file as string}</span>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  // ── Generic array (orders, etc.) ───────────────────────────────────────
  if (Array.isArray(data)) {
    return (
      <div className="px-3 py-2.5 bg-[#0D0D0D] border border-[#1A1A1A] rounded-lg">
        <p className="text-[10px] text-[#555] uppercase tracking-widest font-semibold mb-1.5">
          {formatToolName(result.tool)} ({(data as unknown[]).length})
        </p>
        <div className="space-y-1 max-h-[200px] overflow-y-auto">
          {(data as Record<string, unknown>[]).slice(0, 15).map((item, i) => (
            <div key={i} className="text-[11px] text-[#AAA] flex items-center gap-2">
              <span className="w-1 h-1 rounded-full bg-[#444] shrink-0" />
              <span className="truncate flex-1">{(item.name as string) || (item.item_name as string) || (item.id as string) || JSON.stringify(item).slice(0, 60)}</span>
              {Boolean(item.status) && (
                <span className={`text-[9px] px-1.5 py-0.5 rounded-full shrink-0 ${
                  item.status === 'confirmed' ? 'bg-emerald-500/10 text-emerald-400'
                  : item.status === 'failed' || item.status === 'refunded' ? 'bg-red-500/10 text-red-400'
                  : 'bg-[#1A1A1A] text-[#666]'
                }`}>
                  {item.status as string}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    )
  }

  return null
}

function formatToolName(name: string): string {
  return name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}
